import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import type { TaskInboxItem } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner } from '../../ui/components';

export function Inbox() {
  const listQ = useApiQuery<{ items: TaskInboxItem[] }>('/api/v1/staffdesk/me/inbox?limit=100');
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const markDone = async (item: TaskInboxItem) => {
    setBusy(item.id);
    setActionError(null);
    try {
      await api.post(`/api/v1/staffdesk/inbox-items/${item.id}/done`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not mark item done');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="My inbox"
        subtitle="Open work pushed to you from every module — FlowEngine cases, referrals and more"
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Inbox zero"
            hint="Nothing open right now — new work items appear here as other modules assign them to you."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Priority</th>
                <th className="table-head">Item</th>
                <th className="table-head">Source</th>
                <th className="table-head">Due</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50">
                  <td className="table-cell tabular-nums">{item.priority}</td>
                  <td className="table-cell font-medium">{item.title}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {item.sourceModule === 'c5' ? (
                      <Link to={`/cases/${item.sourceRef}`} className="text-brand-600 underline">
                        {item.sourceModule} · {item.sourceRef}
                      </Link>
                    ) : (
                      `${item.sourceModule} · ${item.sourceRef}`
                    )}
                  </td>
                  <td className="table-cell text-slate-500">{item.dueAt ? formatDateTime(item.dueAt) : '—'}</td>
                  <td className="table-cell text-right">
                    <button
                      className="btn-secondary !px-2.5 !py-1 text-xs"
                      onClick={() => markDone(item)}
                      disabled={busy === item.id}
                    >
                      {busy === item.id ? 'Marking…' : 'Mark done'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
